/**
 * SOLID Review
 * S — Strong. One concern: the account's credits — the checkout, the balance after a purchase, the pushed balance, whose push
 *     to hear — as use cases built once.
 * O — Good. A new payment act is one use case built here.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the payment ops of `ClientUseCases`, and the delivery of `creditsUpdated`.
 * D — Good. Three port interfaces and the shared steps, injected; what it builds is held by role (`IUseCase`).
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { IEntityCheckout, IEntityBalance, IPayloadCreditsUpdated } from '../../domain/ports/schemas/PaymentMoves';
import type { IPortClientPaymentServer, IPortClientServerWatch } from '../ports/contracts';
import type { TPortOutcome, TPortResult } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { ClientSteps } from '../shared/ClientSteps';
import { PassThroughUseCase } from '../shared/PassThroughUseCase';
import type { IConfigDepPayment } from './IConfigDepPayment';
import { ReadBalanceUseCase } from './ReadBalanceUseCase';
import { WatchAccountUseCase } from './WatchAccountUseCase';
import { CreditsUpdatedUseCase } from './CreditsUpdatedUseCase';

export interface IConfigDepPaymentContext {
  readonly decisions: IPortClientDecisions;
  readonly payments: IPortClientPaymentServer;
  /** The account watch — the pushed balance's subscription. */
  readonly watch: IPortClientServerWatch;
  /** The one serial section, shared with every other context. */
  readonly steps: ClientSteps;
}

/**
 * THE ACCOUNT'S CREDITS (frontend strict-ports plan, FE4c, 2026-10-04; use cases since frontend-application-oo P4).
 * BuyCreditsButton called the legacy auth adapter's checkout from React; PaymentSuccessScreen polled its `getMe` and wrote both
 * stores; AppRoot subscribed to the pushed balance and wrote both stores again. Where a balance is kept is one decision.
 */
export class PaymentContext {
  /** "Buy": Stripe's checkout for a credits pack — where the browser goes next. */
  readonly startCheckout: IUseCase<ICommandNothing, TPortResult<IEntityCheckout>>;
  readonly readBalance: IUseCase<ICommandNothing, TPortResult<IEntityBalance>>;
  readonly watchAccount: IUseCase<ICommandNothing, TPortOutcome>;
  readonly unwatchAccount: IUseCase<ICommandNothing, TPortOutcome>;
  /** A pushed balance, delivered — the handler `ServerEvents` hands a `creditsUpdated` to. */
  readonly creditsUpdated: IUseCase<IPayloadCreditsUpdated, TPortOutcome>;

  constructor(deps: IConfigDepPaymentContext) {
    const { payments, watch } = deps;
    const credits: IConfigDepPayment = { ...deps, step: deps.steps.onSession };
    this.startCheckout = new PassThroughUseCase<ICommandNothing, TPortResult<IEntityCheckout>>((ctx) => payments.createCheckout(ctx));
    this.readBalance = new ReadBalanceUseCase(credits);
    this.watchAccount = new WatchAccountUseCase(credits);
    this.unwatchAccount = new PassThroughUseCase<ICommandNothing, TPortOutcome>((ctx) => watch.unwatchAccount(ctx));
    this.creditsUpdated = new CreditsUpdatedUseCase(credits);
  }
}
